// React hooks built on top of the Tauri IPC wrappers.
//
// useCohorts → loads bundled JSON once per session, exposes derived selectors.
// useHealth → polls probe_health every 10s; emits the latest snapshot.
// useLensRoute → reads the active lens from the URL hash for cross-route sync.

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  type Completion,
  type EmailSendResult,
  type KycEvent,
  listSendLogs,
  pollCompletions,
  pollKycEvents,
  readSendLog,
} from "./email";
import {
  recipientFromSendLog,
  type CohortRecipient,
} from "./cohort";
import { probeHealth, readCohorts } from "./tauri";
import type {
  CohortBundle,
  FunnelSnapshot,
  HealthSnapshot,
  Lens,
  MnoRedemption,
} from "./types";

const HEALTH_POLL_MS = 10_000;
const COMPLETIONS_POLL_MS = 10_000;

export function useCohorts(): { bundle: CohortBundle | null; loading: boolean } {
  const [bundle, setBundle] = useState<CohortBundle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    readCohorts()
      .then((b) => {
        if (alive) setBundle(b);
      })
      .catch((e) => {
        // Non-fatal — render the dashboard with stub numbers if cohorts fail.
        console.warn("[bvndle-cc] cohort read failed", e);
        if (alive) setBundle({ data: {}, errors: { _load: String(e) } });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { bundle, loading };
}

export function useHealth(): HealthSnapshot | null {
  const [snap, setSnap] = useState<HealthSnapshot | null>(null);

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const next = await probeHealth();
        if (alive) setSnap(next);
      } catch (e) {
        console.warn("[bvndle-cc] health probe failed", e);
      }
      if (alive) {
        timer = window.setTimeout(tick, HEALTH_POLL_MS);
      }
    };
    tick();

    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return snap;
}

/** Derive a typed funnel snapshot from the cohort bundle. */
export function useFunnel(bundle: CohortBundle | null): FunnelSnapshot | null {
  return useMemo(() => {
    if (!bundle) return null;
    const raw = bundle.data["funnel"];
    return (raw as FunnelSnapshot) ?? null;
  }, [bundle]);
}

/** Derive a typed MNO redemption summary from the cohort bundle. */
export function useMnoRedemption(bundle: CohortBundle | null): MnoRedemption | null {
  return useMemo(() => {
    if (!bundle) return null;
    const raw = bundle.data["mno_redemption"];
    return (raw as MnoRedemption) ?? null;
  }, [bundle]);
}

// ---- live cohort (send-log + completions polling) -------------------------
//
// A wizard-initiated cohort gets a send-log file on disk. This hook reads
// that file once on mount, then polls poll_completions every 10s and merges
// the latest completions with the send-log to produce live CohortRecipient
// rows. Returns null when the cohort id has no send-log on disk (i.e. it's a
// seed cohort and the existing bundled JSON should be used instead).

export interface LiveCohortState {
  recipients: CohortRecipient[];
  lastPolledAt: string | null;
  isLive: boolean;
  completionCount: number;
  failureCount: number;
}

export function useLiveCohort(cohortId: string | undefined): LiveCohortState | null {
  const [sendLog, setSendLog] = useState<EmailSendResult[] | null>(null);
  const [completions, setCompletions] = useState<Map<string, Completion>>(new Map());
  const [lastPolledAt, setLastPolledAt] = useState<string | null>(null);
  const lastCompletionTsRef = useRef<string | undefined>(undefined);

  // Load the send-log once per cohort id. Null means "no log on disk" = seed.
  useEffect(() => {
    if (!cohortId) return;
    let alive = true;
    readSendLog(cohortId)
      .then((entries) => {
        if (!alive) return;
        setSendLog(entries.length > 0 ? entries : null);
      })
      .catch((e) => {
        console.warn("[bvndle-cc] read_send_log failed", e);
        if (alive) setSendLog(null);
      });
    return () => {
      alive = false;
    };
  }, [cohortId]);

  // Poll completions every 10s while the cohort is live (i.e. has a send-log).
  // Sends-since pointer means we only ever pull the delta from the relay.
  useEffect(() => {
    if (!sendLog) return;
    let alive = true;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const newest = await pollCompletions(lastCompletionTsRef.current);
        if (!alive) return;
        if (newest.length > 0) {
          setCompletions((prev) => {
            const next = new Map(prev);
            for (const c of newest) {
              next.set(c.uuid, c);
              if (
                !lastCompletionTsRef.current ||
                c.completed_at > lastCompletionTsRef.current
              ) {
                lastCompletionTsRef.current = c.completed_at;
              }
            }
            return next;
          });
        }
        setLastPolledAt(new Date().toISOString());
      } catch (e) {
        console.warn("[bvndle-cc] poll_completions failed", e);
      }
      if (alive) timer = window.setTimeout(tick, COMPLETIONS_POLL_MS);
    };
    tick();

    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [sendLog]);

  // Merge send-log rows with completion data into CohortRecipient shape.
  return useMemo(() => {
    if (!sendLog) return null;
    const recipients = sendLog.map((entry) => {
      const completion = entry.signup_uuid
        ? completions.get(entry.signup_uuid)
        : undefined;
      return recipientFromSendLog(
        {
          recipient_id: entry.recipient_id,
          email: entry.email,
          status: entry.status,
          provider_message_id: entry.provider_message_id,
          signup_url: entry.signup_url,
          signup_uuid: entry.signup_uuid,
          error: entry.error,
        },
        completion ? { completed_at: completion.completed_at, tag_id: completion.tag_id } : undefined,
      );
    });
    return {
      recipients,
      lastPolledAt,
      isLive: true,
      completionCount: recipients.filter((r) => r.status === "tag_minted").length,
      failureCount: recipients.filter((r) => r.status === "failed").length,
    };
  }, [sendLog, completions, lastPolledAt]);
}

// ---- live cohort index (which cohorts have send-logs?) --------------------

export function useLiveCohortIds(): Set<string> {
  const [ids, setIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    listSendLogs()
      .then((list) => {
        if (alive) setIds(new Set(list));
      })
      .catch((e) => {
        console.warn("[bvndle-cc] list_send_logs failed", e);
      });
    return () => {
      alive = false;
    };
  }, []);
  return ids;
}

/**
 * For each wizard-initiated cohort with a send-log on disk, return a summary
 * the index page can render as a card: total recipients, how many sent
 * successfully, how many marked minted (from polled completions).
 *
 * Polling runs at the cohort-detail level too — this hook just runs it once
 * at the index so the cards show non-zero MINTED counts when applicable.
 */
export interface LiveCohortSummary {
  id: string;
  total: number;
  sent: number;
  failed: number;
  minted: number;
  signup_uuids: string[];
}

export function useLiveCohortSummaries(): LiveCohortSummary[] {
  const [summaries, setSummaries] = useState<LiveCohortSummary[]>([]);
  const [completions, setCompletions] = useState<Set<string>>(new Set());

  // 1. Discover live cohort ids + read each send-log once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ids = await listSendLogs();
        const built: LiveCohortSummary[] = [];
        for (const id of ids) {
          try {
            const log = await readSendLog(id);
            built.push({
              id,
              total: log.length,
              sent: log.filter((r) => r.status === "sent").length,
              failed: log.filter((r) => r.status === "failed").length,
              minted: 0, // filled in once completions poll returns
              signup_uuids: log.map((r) => r.signup_uuid ?? "").filter(Boolean),
            });
          } catch (e) {
            console.warn(`[bvndle-cc] read_send_log(${id}) failed`, e);
          }
        }
        if (alive) setSummaries(built);
      } catch (e) {
        console.warn("[bvndle-cc] list_send_logs failed", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 2. Poll completions periodically; accumulate matched UUIDs across all
  //    live cohorts so each card's MINTED count is current.
  useEffect(() => {
    if (summaries.length === 0) return;
    let alive = true;
    let timer: number | undefined;
    let lastSeen: string | undefined;

    const tick = async () => {
      try {
        const newest = await pollCompletions(lastSeen);
        if (!alive) return;
        if (newest.length > 0) {
          setCompletions((prev) => {
            const next = new Set(prev);
            for (const c of newest) {
              next.add(c.uuid);
              if (!lastSeen || c.completed_at > lastSeen) lastSeen = c.completed_at;
            }
            return next;
          });
        }
      } catch (e) {
        console.warn("[bvndle-cc] poll_completions(index) failed", e);
      }
      if (alive) timer = window.setTimeout(tick, COMPLETIONS_POLL_MS);
    };
    tick();
    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [summaries.length]);

  // 3. Re-derive minted counts as completions accumulate.
  return useMemo(
    () =>
      summaries.map((s) => ({
        ...s,
        minted: s.signup_uuids.filter((u) => completions.has(u)).length,
      })),
    [summaries, completions],
  );
}

// ---- KYC events polling (mirrors completions polling) -------------------
//
// Polls /kyc-events on the same Worker every 10s. Returns the running list
// keyed by uuid so the audit timeline can flip the muted MetaMap
// placeholder rows to real entries the moment verification webhooks land.

export function useKycEvents(): Map<string, KycEvent> {
  const [events, setEvents] = useState<Map<string, KycEvent>>(new Map());
  const lastSeenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const newest = await pollKycEvents(lastSeenRef.current);
        if (!alive) return;
        if (newest.length > 0) {
          setEvents((prev) => {
            const next = new Map(prev);
            for (const e of newest) {
              next.set(`${e.uuid}:${e.verification_id}`, e);
              if (!lastSeenRef.current || e.completed_at > lastSeenRef.current) {
                lastSeenRef.current = e.completed_at;
              }
            }
            return next;
          });
        }
      } catch (e) {
        // Silent: the kyc-webhook endpoint may not be deployed yet on
        // older Worker versions. Treat as "no events" rather than noise.
        // (Worker handler ships in v0.8.3.)
        void e;
      }
      if (alive) timer = window.setTimeout(tick, COMPLETIONS_POLL_MS);
    };
    tick();

    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return events;
}

/**
 * Active audience lens — derived from the hash route (/lens/:id) or from a
 * `?lens=` query param on any other page. Defaults to "dev" (full chrome).
 */
export function useActiveLens(): Lens {
  const location = useLocation();
  return useMemo(() => {
    const match = location.pathname.match(/\/lens\/(chairman|regulator|partner|dev)/);
    if (match) return match[1] as Lens;
    const params = new URLSearchParams(location.search);
    const queried = params.get("lens");
    if (queried === "chairman" || queried === "regulator" || queried === "partner" || queried === "dev") {
      return queried;
    }
    return "dev";
  }, [location.pathname, location.search]);
}
